/**
 * Finanzas → **Exportar Concar** (`/finanzas/exportar-concar`).
 *
 * Puerto de `Reportes::exportar_concar` + `Reportes::generar_excel_concar` (vista
 * `views/menu/reportes/exportar_concar.php` y bundle `src/reports/concar.js`).
 *
 * La pantalla arma el **Excel de asientos del mes** con el formato de importación de
 * CONCAR (41 columnas, de la A a la AO) o la **plantilla vacía**, y ordena los
 * **subdiarios** que CONCAR necesita cuando el período no cabe en un solo libro.
 *
 * Reglas heredadas:
 * - El asiento de cada venta lleva el DEBE del cobro (por cada método de pago, si
 *   se pagó con dos), el HABER del IGV y un HABER por producto.
 * - El comprobante se numera `MM` + correlativo de cuatro dígitos y, al pasar de
 *   9 999, se abre un **libro** nuevo (una hoja más) con el siguiente subdiario.
 * - Las ventas anuladas entran con los importes en cero.
 *
 * Diferencias anotadas:
 * - Antes de descargar se ve el **resumen** del período (comprobantes, asientos y
 *   libros), así se sabe si va a salir una hoja o varias antes de abrir el archivo.
 * - La plantilla y el archivo con datos son el mismo botón con una casilla; en el
 *   original era un `window.location` a `Reportes/generar_excel_concar`.
 */

import { Download, FileSpreadsheet, Info, Layers, Settings2 } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CampoFecha } from '@/components/ui/calendario';
import { Checkbox } from '@/components/ui/checkbox';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { abrirPdfAutenticado } from '@/lib/descargas';
import { ApiError } from '@/lib/http/api-error';

import { SubdiariosDialog } from '../components/subdiarios-dialog';
import { concarApi, useResumenConcar, useSubdiariosConcar } from '../exportar-concar.api';

/** El mes en curso en `AAAA-MM`. */
function mesActual(): string {
  const hoy = new Date();

  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}`;
}

export function ExportarConcarPage() {
  useDocumentTitle('Finanzas · Exportar Concar');

  const [periodo, setPeriodo] = useState(mesActual);
  const [conDatos, setConDatos] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogo, setDialogo] = useState(false);

  const resumen = useResumenConcar(periodo);
  const subdiarios = useSubdiariosConcar();

  const descargar = async () => {
    setError(null);
    setDescargando(true);

    const compacto = periodo.replace('-', '');
    const nombre = `CONCAR_${conDatos ? 'Datos' : 'Plantilla'}_${compacto}.xlsx`;

    try {
      await abrirPdfAutenticado(concarApi.urlExcel(periodo, conDatos), { nombre });
    } catch (fallo) {
      setError(
        fallo instanceof ApiError
          ? fallo.message
          : fallo instanceof Error
            ? fallo.message
            : 'No se pudo generar el Excel.',
      );
    } finally {
      setDescargando(false);
    }
  };

  const datos = resumen.data;
  const activos = subdiarios.data?.activos ?? [];

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">Exportar Concar</h1>
          <p className="mt-1 text-sm text-ink-500">
            Genera el Excel con los asientos del mes en el formato de importación de CONCAR, o la
            plantilla vacía para llenarla a mano.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            leftIcon={<Download className="size-4" aria-hidden="true" />}
            loading={descargando}
            onClick={() => void descargar()}
          >
            {conDatos ? 'Descargar asientos del mes' : 'Descargar plantilla'}
          </Button>
          <Button
            variant="outline"
            leftIcon={<Settings2 className="size-4" aria-hidden="true" />}
            onClick={() => setDialogo(true)}
          >
            Gestionar subdiarios
          </Button>
        </div>
      </header>

      {error ? (
        <Alert variant="error" title="No se pudo generar el Excel">
          {error}
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        {/* Período y qué se descarga */}
        <Card className="xl:col-span-2">
          <CardContent className="space-y-4 p-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-xs font-medium text-ink-500">
                Período
                <CampoFecha
                  tipo="month"
                  value={periodo}
                  onChange={(evento) => setPeriodo(evento.target.value)}
                  aria-label="Período"
                />
              </label>

              <div className="flex flex-col justify-center gap-2 pt-4">
                <Checkbox
                  label="Generar con los datos de ventas del período"
                  checked={conDatos}
                  onChange={(evento) => setConDatos(evento.target.checked)}
                />
                <span className="text-xs text-ink-500">
                  {conDatos
                    ? 'El archivo incluye los asientos de las ventas del mes.'
                    : 'Sólo se descarga la plantilla con las 41 columnas de CONCAR.'}
                </span>
              </div>
            </div>

            <div className="rounded-card bg-ink-50/60 p-3 text-xs text-ink-600">
              <p className="mb-2 font-semibold text-ink-700">Qué lleva cada asiento</p>
              <ul className="space-y-1">
                <li>
                  <strong>DEBE</strong>: la cuenta de cobro de cada método de pago (dos líneas si
                  la venta se pagó con dos).
                </li>
                <li>
                  <strong>HABER</strong>: el IGV por pagar (<code className="font-mono">401111</code>)
                  y una línea por producto con su código contable.
                </li>
                <li>
                  Los comprobantes anulados entran con los importes en cero y el anexo{' '}
                  <code className="font-mono">0001</code>.
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Resumen del período */}
        <Card>
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center gap-2">
              <Layers className="size-4 text-brand-500" aria-hidden="true" />
              <h2 className="text-base font-semibold text-ink-900">Resumen del período</h2>
            </div>

            {resumen.isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-24" />
              </div>
            ) : resumen.isError ? (
              <Alert variant="warning" title="No se pudo revisar el período">
                {resumen.error instanceof ApiError
                  ? resumen.error.message
                  : 'Inténtalo de nuevo en unos segundos.'}
              </Alert>
            ) : (
              <>
                <dl className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      Comprobantes
                    </dt>
                    <dd className="text-lg font-bold text-ink-900 tabular-nums">
                      {(datos?.comprobantes ?? 0).toLocaleString('es-PE')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      Asientos
                    </dt>
                    <dd className="text-lg font-bold text-ink-900 tabular-nums">
                      {(datos?.asientos ?? 0).toLocaleString('es-PE')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      Hojas (libros)
                    </dt>
                    <dd className="text-lg font-bold text-ink-900 tabular-nums">
                      {datos?.libros ?? 0}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      Subdiarios
                    </dt>
                    <dd className="text-sm font-semibold text-ink-700">{activos.join(', ') || '—'}</dd>
                  </div>
                </dl>

                {datos && datos.comprobantes === 0 ? (
                  <Alert variant="warning" title="Ese mes no tiene ventas">
                    La descarga saldría con las columnas vacías: revisa el período.
                  </Alert>
                ) : null}

                {datos && datos.libros > 1 ? (
                  <Alert variant="info" title={`Salen ${datos.libros} hojas`}>
                    El período pasa de 9 999 comprobantes, así que se abre un libro por bloque, con
                    re numeración y el subdiario que le toca a cada uno.
                  </Alert>
                ) : null}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <span className="rounded-control bg-brand-50 p-2 text-brand-600">
                <FileSpreadsheet className="size-5" aria-hidden="true" />
              </span>
              <div>
                <h2 className="text-base font-semibold text-ink-900">Subdiarios de CONCAR</h2>
                <p className="text-sm text-ink-500">
                  Orden en el que se usan los subdiarios cuando el período necesita más de un libro.
                </p>
              </div>
            </div>

            <Button variant="outline" size="sm" onClick={() => setDialogo(true)}>
              Gestionar subdiarios
            </Button>
          </div>

          <div className="flex flex-wrap gap-2">
            {(subdiarios.data?.subdiarios ?? []).length === 0 ? (
              <p className="text-sm text-ink-500">
                No hay subdiarios configurados: la API usaría{' '}
                {(subdiarios.data?.porDefecto ?? []).join(', ')}.
              </p>
            ) : (
              (subdiarios.data?.subdiarios ?? []).map((subdiario) => (
                <span
                  key={subdiario.id ?? subdiario.codigo_subdiario}
                  className={`rounded-control border px-2.5 py-1 text-xs font-medium ${
                    Number(subdiario.activo) === 1 || subdiario.activo === true
                      ? 'border-brand-200 bg-brand-50 text-brand-700'
                      : 'border-ink-200 bg-ink-50 text-ink-500'
                  }`}
                >
                  <code className="font-mono">{subdiario.codigo_subdiario}</code> ·{' '}
                  {subdiario.nombre}
                  {Number(subdiario.activo) === 1 || subdiario.activo === true ? '' : ' (inactivo)'}
                </span>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      <p className="flex items-start gap-2 text-xs text-ink-500">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        El Excel lleva las tres filas de especificación de CONCAR (campos, restricciones y formato)
        y después los asientos, así que se puede cargar tal cual en el sistema contable o usarlo
        como plantilla.
      </p>

      <SubdiariosDialog open={dialogo} onClose={() => setDialogo(false)} datos={subdiarios.data} />
    </div>
  );
}
